import { useUploadFolderRefresh } from '../../context/progressContext'
import { SignalRContext } from '../../context/signalrContext'
import React, {
	Dispatch,
	SetStateAction,
	useCallback,
	useContext,
	useRef,
} from 'react'
import { AxiosProgressEvent } from 'axios'
import {
	useCreateContent,
	useUploadDocument,
	useUploadToUrl,
} from '../api/documentHooks'
import { getSafeFileNameWithDate } from '../../lib/utils/files/generateFileName'
import { FORBIDDEN_EXTENSIONS } from '../../lib/utils/formatDocument'
import toastCaller, { errors, TOAST_IDS } from '../../lib/utils/toastCaller'
import {
	buildUploadTreeIncrementally,
	generateUploadId,
	getDescendantStatusIds,
	refreshUploadFolderStates,
	UPLOAD_CONSTANTS,
	updateFolderState,
	updateUploadStatus,
	type CreateSafeFolder,
	type UploadTree,
} from '../../lib/utils/upload'
import type {
	UploadDiscoveryState,
	UploadFileStatus,
} from '../../context/progressContext'

interface ManualRenameData {
	originalName: string
	parentId: string
	files: File[]
	relativePaths: string[]
	sessionId: string
	suggestedName?: string
	error: unknown
}

interface UseOptimizedUploadParams {
	currentUserId: string
	setUploadStatuses: Dispatch<SetStateAction<UploadFileStatus[]>>
	setUploadDiscoveryState: Dispatch<SetStateAction<UploadDiscoveryState>>
	onOpenUploadPopup: () => void
	refetch: () => void
	activeFolderId?: string
	enqueueUpload: (task: {
		task: () => Promise<void>
		cancel: () => void
	}) => Promise<void>
	clearQueuedUploads: (cancelActive?: boolean) => void
	globalCancelFlagRef?: React.MutableRefObject<boolean>
	canceledUploadStatusIdsRef?: React.MutableRefObject<Set<string>>
	onManualRenameRequired?: (data: ManualRenameData) => void
}

const isCancelableStatus = (status: UploadFileStatus['status']) =>
	status === 'pending' || status === 'uploading'

const getErrorMessage = (error: any) => {
	const errorCode = error?.response?.data?.errors?.[0] || 'UNKNOWN_ERROR'

	if (errors?.[errorCode]) return errors[errorCode]()
	if (error?.response?.status === 500) {
		return 'Something went wrong on our end. Please try again in few minutes.'
	}
	if (error?.response?.data?.message) return error.response.data.message
	return errorCode !== 'UNKNOWN_ERROR'
		? errorCode
		: error?.message || 'Unknown error'
}

export const useOptimizedUpload = ({
	currentUserId,
	setUploadStatuses,
	setUploadDiscoveryState,
	onOpenUploadPopup,
	refetch,
	activeFolderId,
	enqueueUpload,
	clearQueuedUploads,
	globalCancelFlagRef,
	canceledUploadStatusIdsRef,
	onManualRenameRequired,
}: UseOptimizedUploadParams) => {
	const { refresh: refreshVisibleUploadFolder } = useUploadFolderRefresh()
	const socket = useContext(SignalRContext)
	const socketRef = useRef(socket)
	socketRef.current = socket
	const { mutateAsync: createContent } = useCreateContent()
	const { mutateAsync: uploadToUrl } = useUploadToUrl()
	const { mutateAsync: uploadDocument } = useUploadDocument()
	const localCanceledStatusIdsRef = useRef(new Set<string>())
	const canceledStatusIdsRef =
		canceledUploadStatusIdsRef || localCanceledStatusIdsRef
	const storageQuotaExceededRef = useRef(false)
	const usedFileNamesRef = useRef(new Set<string>())
	const activeFolderIdRef = useRef(activeFolderId)
	const refetchRef = useRef(refetch)

	activeFolderIdRef.current = activeFolderId
	refetchRef.current = refetch

	const isCanceled = (statusId: string) =>
		globalCancelFlagRef?.current || canceledStatusIdsRef.current.has(statusId)

	const refreshFolder = (folderStatusId: string) => {
		setUploadStatuses((previousStatuses) =>
			updateFolderState(previousStatuses, folderStatusId),
		)
	}

	const refreshActiveFolderAfterUpload = (parentFolderId: string) => {
		// A queued upload may outlive the component that started it.
		if (refreshVisibleUploadFolder(parentFolderId)) return
		if (
			activeFolderIdRef.current === parentFolderId &&
			!socketRef.current?.canUseFolderEvents(parentFolderId)
		)
			refetchRef.current()
	}

	const cancelBranch = useCallback(
		(statusId: string, error?: string) => {
			setUploadStatuses((previousStatuses) => {
				const statusIds = [
					statusId,
					...getDescendantStatusIds(previousStatuses, statusId),
				]
				const idsToCancel = new Set(statusIds)
				statusIds.forEach((id) => canceledStatusIdsRef.current.add(id))

				let nextStatuses = previousStatuses.map((status) => {
					if (!status.id || !idsToCancel.has(status.id)) return status
					status.abortController?.abort()
					return isCancelableStatus(status.status)
						? { ...status, status: 'canceled' as const, error }
						: status
				})

				return refreshUploadFolderStates(nextStatuses)
			})
		},
		[setUploadStatuses],
	)

	const uploadSingleFile = async (
		status: UploadFileStatus,
		folderId: string,
	): Promise<void> => {
		if (!status.id || !status.file || isCanceled(status.id)) return

		const { file, id: statusId, abortController } = status
		let storageKey = ''

		try {
			const extension = file.name
				.substring(file.name.lastIndexOf('.'))
				.toLowerCase()
			if (FORBIDDEN_EXTENSIONS.includes(extension)) {
				const error = `File type "${extension}" is not allowed for security reasons.`
				setUploadStatuses((previousStatuses) =>
					updateUploadStatus(previousStatuses, statusId, {
						status: 'canceled',
						error,
					}),
				)
				toastCaller({
					type: 'warning',
					message: error,
					toastId: TOAST_IDS.UPLOAD,
					update: true,
				})
				return
			}

			setUploadStatuses((previousStatuses) =>
				updateUploadStatus(previousStatuses, statusId, {
					status: 'uploading',
					currentFolderId: folderId,
				}),
			)

			const createContentResponse = await createContent({
				size: file.size,
				signal: abortController?.signal,
			})
			if (isCanceled(statusId)) return

			const uploadUrl = createContentResponse?.data?.data?.uploadUrl
			storageKey = createContentResponse?.data?.data?.storageKey || ''
			if (!uploadUrl || !storageKey) throw new Error('Missing upload data')

			let lastProgress = 0
			await uploadToUrl({
				file,
				uploadUrl,
				signal: abortController?.signal,
				onUploadProgress: ({ loaded, total }: AxiosProgressEvent) => {
					if (!total || isCanceled(statusId)) return
					const progress = Math.round((loaded * 100) / total)
					if (
						progress - lastProgress <
							UPLOAD_CONSTANTS.PROGRESS_UPDATE_THRESHOLD &&
						progress !== 100
					) {
						return
					}
					lastProgress = progress
					setUploadStatuses((previousStatuses) =>
						updateUploadStatus(previousStatuses, statusId, { progress }),
					)
				},
			})
			if (isCanceled(statusId)) return

			const uploadResponse = await uploadDocument({
				data: {
					name: file.name,
					storageKey,
					parentId: folderId,
					userId: currentUserId,
				},
				invalidate: false,
				withoutErrorToast: true,
				signal: abortController?.signal,
			})
			if (isCanceled(statusId)) return

			usedFileNamesRef.current.add(file.name)
			setUploadStatuses((previousStatuses) =>
				updateUploadStatus(previousStatuses, statusId, {
					status: 'completed',
					progress: 100,
					fileId: uploadResponse?.data?.data?.id || '',
					storageKey,
				}),
			)
		} catch (error: any) {
			if (isCanceled(statusId)) return
			let uploadError = error

			if (
				error?.name === 'CanceledError' ||
				error?.message?.includes('cancel')
			) {
				setUploadStatuses((previousStatuses) =>
					updateUploadStatus(previousStatuses, statusId, {
						status: 'canceled',
					}),
				)
				return
			}

			if (error?.response?.data?.errors?.[0] === 'STORAGE_QUOTA_EXCEEDED') {
				storageQuotaExceededRef.current = true
				toastCaller({
					type: 'warning',
					errorMessageCode: 'STORAGE_QUOTA_EXCEEDED',
					toastId: TOAST_IDS.UPLOAD,
					update: true,
				})
				setUploadStatuses((previousStatuses) => {
					let nextStatuses = previousStatuses.map((item) =>
						isCancelableStatus(item.status)
							? {
									...item,
									status: 'failed' as const,
									error: 'Storage quota exceeded',
								}
							: item,
					)
					return refreshUploadFolderStates(nextStatuses)
				})
				return
			}

			if (error?.response?.data?.errors?.[0] === 'FILE_SAME_NAME_EXISTS') {
				const suggestedName =
					error?.response?.data?.data?.name ??
					getSafeFileNameWithDate(file.name, false, false)
				const lastDot = suggestedName.lastIndexOf('.')
				const hasExtension = lastDot > 0 && lastDot < suggestedName.length - 1
				const extension = hasExtension ? suggestedName.substring(lastDot) : ''
				const baseName = (
					hasExtension ? suggestedName.substring(0, lastDot) : suggestedName
				).replace(/\s*\(\d+\)$/, '')

				let retryAttempt = 0
				let nextName = suggestedName
				while (retryAttempt < 100 && !isCanceled(statusId)) {
					while (usedFileNamesRef.current.has(nextName) && retryAttempt < 100) {
						nextName = `${baseName} (${++retryAttempt})${extension}`
					}

					try {
						const response = await uploadDocument({
							data: {
								name: nextName,
								storageKey,
								parentId: folderId,
								userId: currentUserId,
							},
							invalidate: false,
							withoutErrorToast: true,
							signal: abortController?.signal,
						})
						if (isCanceled(statusId)) return

						usedFileNamesRef.current.add(nextName)
						toastCaller({
							type: 'warning',
							message: `File already exists! Renamed to "${nextName}"`,
							toastId: TOAST_IDS.UPLOAD,
							update: true,
						})
						setUploadStatuses((previousStatuses) =>
							updateUploadStatus(previousStatuses, statusId, {
								file: new File([file], nextName, { type: file.type }),
								originalFileName: nextName,
								status: 'completed',
								progress: 100,
								storageKey,
								fileId: response?.data?.data?.id || '',
							}),
						)
						return
					} catch (retryError: any) {
						uploadError = retryError
						if (
							retryError?.response?.data?.errors?.[0] !==
							'FILE_SAME_NAME_EXISTS'
						) {
							break
						}
						nextName = `${baseName} (${++retryAttempt})${extension}`
					}
				}
			}

			setUploadStatuses((previousStatuses) =>
				updateUploadStatus(previousStatuses, statusId, {
					status: 'failed',
					error: getErrorMessage(uploadError),
				}),
			)
		}
	}

	const uploadFiles = async (
		statuses: UploadFileStatus[],
		folderId: string,
	): Promise<void> => {
		let nextIndex = 0
		const workerCount = Math.min(
			UPLOAD_CONSTANTS.MAX_CONCURRENT_UPLOADS,
			statuses.length,
		)

		await Promise.all(
			Array.from({ length: workerCount }, async () => {
				while (
					nextIndex < statuses.length &&
					!storageQuotaExceededRef.current
				) {
					const status = statuses[nextIndex++]
					if (status && !isCanceled(status.id || '')) {
						await uploadSingleFile(status, folderId)
					}
				}
			}),
		)
	}

	const processOptimizedUpload = async (
		tree: UploadTree,
		statuses: UploadFileStatus[],
		sessionId: string,
		baseFolderId: string,
		createSafeFolder: CreateSafeFolder,
	) => {
		if (!baseFolderId) return

		storageQuotaExceededRef.current = false
		usedFileNamesRef.current.clear()
		const statusesById = new Map(statuses.map((status) => [status.id, status]))

		const stopFolderBranch = (
			folderStatusId: string,
			status: 'failed' | 'canceled',
			error?: string,
		) => {
			canceledStatusIdsRef.current.add(folderStatusId)
			setUploadStatuses((previousStatuses) => {
				const descendantIds = new Set(
					getDescendantStatusIds(previousStatuses, folderStatusId),
				)
				descendantIds.forEach((id) => canceledStatusIdsRef.current.add(id))
				const nextStatuses = previousStatuses.map((item) => {
					if (item.id === folderStatusId) return { ...item, status, error }
					if (
						item.id &&
						descendantIds.has(item.id) &&
						isCancelableStatus(item.status)
					) {
						item.abortController?.abort()
						return { ...item, status: 'canceled' as const, error }
					}
					return item
				})
				return refreshUploadFolderStates(nextStatuses)
			})
		}

		const processFolder = async (
			folderStatusId: string,
			parentFolderId: string,
		): Promise<void> => {
			const folder = statusesById.get(folderStatusId)
			if (
				!folder ||
				isCanceled(folderStatusId) ||
				storageQuotaExceededRef.current
			)
				return

			setUploadStatuses((previousStatuses) =>
				updateUploadStatus(previousStatuses, folderStatusId, {
					status: 'uploading',
					currentFolderId: parentFolderId,
				}),
			)

			let createdFolder
			try {
				createdFolder = await createSafeFolder({
					name:
						folder.folderName || folder.originalFileName || 'Untitled folder',
					parentId: parentFolderId,
					invalidate: false,
					autoRename: true,
				})
			} catch (error) {
				stopFolderBranch(folderStatusId, 'failed', getErrorMessage(error))
				return
			}

			if (isCanceled(folderStatusId)) {
				stopFolderBranch(folderStatusId, 'canceled')
				return
			}

			if (!createdFolder?.id) {
				const error = createdFolder?.manualRenameRequired
					? 'Manual rename required'
					: 'Failed to create folder'
				stopFolderBranch(folderStatusId, 'failed', error)

				if (createdFolder?.manualRenameRequired && onManualRenameRequired) {
					const descendantFiles = getDescendantStatusIds(
						statuses,
						folderStatusId,
					)
						.map((id) => statusesById.get(id))
						.filter(
							(item): item is UploadFileStatus =>
								item?.nodeType === 'file' && Boolean(item.file),
						)
					onManualRenameRequired({
						originalName: folder.originalFileName || folder.folderName || '',
						parentId: parentFolderId,
						files: descendantFiles.map((item) => item.file!),
						relativePaths: descendantFiles.map(
							(item) => item.relativePath || item.file!.name,
						),
						sessionId,
						suggestedName: createdFolder.suggestedName,
						error: createdFolder.error,
					})
				}
				return
			}

			if (createdFolder.name !== folder.folderName) {
				toastCaller({
					type: 'warning',
					message: `Folder "${folder.folderName}" was renamed to "${createdFolder.name}"`,
					toastId: TOAST_IDS.UPLOAD,
					update: true,
				})
			}

			setUploadStatuses((previousStatuses) =>
				updateUploadStatus(previousStatuses, folderStatusId, {
					status: 'uploading',
					folderName: createdFolder.name,
					currentFolderId: createdFolder.id!,
					createdOnServer: true,
				}),
			)
			refreshActiveFolderAfterUpload(parentFolderId)

			const children = (folder.childrenIds || [])
				.map((id) => statusesById.get(id))
				.filter((item): item is UploadFileStatus => Boolean(item))
			const directFiles = children.filter((item) => item.nodeType === 'file')
			const childFolders = children.filter((item) => item.nodeType === 'folder')

			await uploadFiles(directFiles, createdFolder.id)
			for (const childFolder of childFolders) {
				if (isCanceled(folderStatusId) || storageQuotaExceededRef.current) break
				await processFolder(childFolder.id!, createdFolder.id)
			}

			refreshFolder(folderStatusId)
			refreshActiveFolderAfterUpload(createdFolder.id)
		}

		const rootNodes = tree.rootStatusIds
			.map((id) => statusesById.get(id))
			.filter((item): item is UploadFileStatus => Boolean(item))
		const rootFolders = rootNodes.filter((item) => item.nodeType === 'folder')
		const rootFiles = rootNodes.filter((item) => item.nodeType === 'file')

		for (const rootFolder of rootFolders) {
			if (storageQuotaExceededRef.current || isCanceled(rootFolder.id || ''))
				break
			await processFolder(rootFolder.id!, baseFolderId)
		}
		if (!storageQuotaExceededRef.current)
			await uploadFiles(rootFiles, baseFolderId)

		if (rootNodes.length > 0) {
			refreshActiveFolderAfterUpload(baseFolderId)
		}
	}

	const handleOptimizedUpload = async (
		files: File[],
		relativePaths: string[],
		baseFolderId: string,
		createSafeFolder: CreateSafeFolder,
	) => {
		if (!files.length || !baseFolderId) return

		onOpenUploadPopup()
		if (globalCancelFlagRef) globalCancelFlagRef.current = false

		const sessionId = generateUploadId('session-')
		const batchCanceledRef = { current: false }
		setUploadDiscoveryState({
			isDiscovering: true,
			rootItemsCount: 0,
			processedFiles: 0,
			totalFiles: files.length,
		})

		const treePromise = buildUploadTreeIncrementally(
			files,
			relativePaths,
			baseFolderId,
			{
				isCanceled: (statusId) =>
					batchCanceledRef.current || isCanceled(statusId),
				onProgress: ({
					processedFiles,
					rootItemsCount,
					statuses,
					totalFiles,
				}) => {
					setUploadStatuses((previousStatuses) => {
						const nextSessionStatuses = statuses.map((status) => ({
							...status,
							sessionId,
						}))
						const sessionStartIndex = previousStatuses.findIndex(
							(status) => status.sessionId === sessionId,
						)
						if (sessionStartIndex === -1) {
							return [...previousStatuses, ...nextSessionStatuses]
						}

						const statusesWithoutSession = previousStatuses.filter(
							(status) => status.sessionId !== sessionId,
						)
						return [
							...statusesWithoutSession.slice(0, sessionStartIndex),
							...nextSessionStatuses,
							...statusesWithoutSession.slice(sessionStartIndex),
						]
					})
					setUploadDiscoveryState({
						isDiscovering: true,
						rootItemsCount,
						processedFiles,
						totalFiles,
					})
				},
			},
		)

		await enqueueUpload({
			task: async () => {
				const tree = await treePromise
				if (batchCanceledRef.current) return

				const statuses = tree.statuses.map((status) => ({
					...status,
					sessionId,
				}))
				setUploadDiscoveryState({
					isDiscovering: false,
					rootItemsCount: tree.rootStatusIds.length,
					processedFiles: files.length,
					totalFiles: files.length,
				})
				await processOptimizedUpload(
					tree,
					statuses,
					sessionId,
					baseFolderId,
					createSafeFolder,
				)
			},
			cancel: () => {
				batchCanceledRef.current = true
			},
		})
	}

	const handleCancel = useCallback(
		(statusId: string) => cancelBranch(statusId),
		[cancelBranch],
	)

	const handleCancelAll = useCallback(() => {
		if (globalCancelFlagRef) globalCancelFlagRef.current = true
		clearQueuedUploads(true)
		setUploadStatuses((previousStatuses) => {
			const nextStatuses = previousStatuses.map((status) => {
				if (!isCancelableStatus(status.status)) return status
				if (status.id) canceledStatusIdsRef.current.add(status.id)
				status.abortController?.abort()
				return { ...status, status: 'canceled' as const }
			})
			return refreshUploadFolderStates(nextStatuses)
		})
	}, [clearQueuedUploads, globalCancelFlagRef, setUploadStatuses])

	return { handleOptimizedUpload, handleCancel, handleCancelAll }
}
